import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import reportApprovalService from '../../services/reportApprovalService';
import ReportReviewModal from './components/ReportReviewModal';
import CommonPagination from '../../components/common/CommonPagination';
import './ReportApprovalQueuePage.css';

export const ReportApprovalQueuePage = () => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({
    totalPending: 0,
    totalCritical: 0,
    totalHeld: 0,
    totalApprovedToday: 0,
    totalAmendmentRequests: 0
  });

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const { user } = useAuth();

  // Determine user clinical department permissions
  const userRole = (user?.roleName || '').toLowerCase();
  const isRadiologist = userRole.includes('radio');
  const isPathologist = userRole.includes('pathol');
  const isMicrobiologist = userRole.includes('micro');
  const isAdmin = userRole.includes('admin') || userRole.includes('director') || userRole.includes('auditor');

  const defaultDept = isRadiologist ? 'Radiology' : isPathologist ? 'Pathology' : isMicrobiologist ? 'Microbiology' : '';

  // Filters
  const [department, setDepartment] = useState(defaultDept);

  useEffect(() => {
    if (isRadiologist) setDepartment('Radiology');
    else if (isPathologist) setDepartment('Pathology');
    else if (isMicrobiologist) setDepartment('Microbiology');
  }, [isRadiologist, isPathologist, isMicrobiologist]);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [isCriticalOnly, setIsCriticalOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const pageSize = 20;

  // Selected item for review modal
  const [reviewTestId, setReviewTestId] = useState(null);

  const showToastMsg = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [queueRes, summaryRes] = await Promise.all([
        reportApprovalService.getApprovalQueue({
          department: department || undefined,
          status: status || undefined,
          priority: priority || undefined,
          isCriticalOnly: isCriticalOnly || undefined,
          searchTerm: searchTerm.trim() || undefined,
          pageIndex,
          pageSize
        }),
        reportApprovalService.getQueueSummary()
      ]);

      if (queueRes?.data) {
        setItems(queueRes.data.items || []);
        setTotalCount(queueRes.data.totalCount || 0);
      }

      if (summaryRes?.data) {
        setSummary(summaryRes.data);
      }
    } catch (err) {
      showToastMsg('Failed to load report approval queue.', 'error');
    } finally {
      setLoading(false);
    }
  }, [department, status, priority, isCriticalOnly, searchTerm, pageIndex]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleQuickFilter = (statusVal, criticalFlag = false) => {
    setStatus(statusVal);
    setIsCriticalOnly(criticalFlag);
    setPageIndex(1);
  };

  const handleUnhold = async (testId) => {
    try {
      await reportApprovalService.unholdReport(testId);
      showToastMsg('Report removed from hold and returned to review.');
      loadData();
    } catch (err) {
      showToastMsg(err.response?.data?.message || 'Failed to unhold report.', 'error');
    }
  };

  return (
    <div className="rep-app-container">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          background: toast.type === 'error' ? '#ef4444' : '#10b981',
          color: '#ffffff',
          borderRadius: '6px',
          zIndex: 9999,
          fontWeight: '600',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div className="rep-app-header">
        <div className="rep-app-title-block">
          <h1>Report Approval & Medical Release Queue</h1>
          <p className="rep-app-subtitle">
            Centralized clinical verification, critical value review, and doctor signature authorization for Pathology, Microbiology, and Radiology.
          </p>
        </div>
        <div>
          <button
            type="button"
            style={{
              background: '#f1f5f9',
              color: '#334155',
              border: '1px solid #cbd5e1',
              padding: '8px 14px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            onClick={loadData}
          >
            ↻ Refresh Worklist
          </button>
        </div>
      </div>

      {/* KPI Metric Summary Cards */}
      <div className="rep-app-metrics-grid">
        <div
          className={`metric-card pending ${status === 'PENDING' ? 'active-filter' : ''}`}
          onClick={() => handleQuickFilter('PENDING')}
        >
          <div className="metric-label">Pending Review</div>
          <div className="metric-value">{summary.totalPending}</div>
        </div>

        <div
          className={`metric-card crit ${isCriticalOnly ? 'active-filter' : ''}`}
          onClick={() => handleQuickFilter('', true)}
        >
          <div className="metric-label">Critical Findings</div>
          <div className="metric-value" style={{ color: '#ef4444' }}>{summary.totalCritical}</div>
        </div>

        <div
          className={`metric-card held ${status === 'HELD' ? 'active-filter' : ''}`}
          onClick={() => handleQuickFilter('HELD')}
        >
          <div className="metric-label">Clinical Hold</div>
          <div className="metric-value" style={{ color: '#8b5cf6' }}>{summary.totalHeld}</div>
        </div>

        <div
          className={`metric-card approved ${status === 'APPROVED' ? 'active-filter' : ''}`}
          onClick={() => handleQuickFilter('APPROVED')}
        >
          <div className="metric-label">Released Today</div>
          <div className="metric-value" style={{ color: '#10b981' }}>{summary.totalApprovedToday}</div>
        </div>

        <div
          className={`metric-card amended ${status === 'UNDER_REVIEW' ? 'active-filter' : ''}`}
          onClick={() => handleQuickFilter('UNDER_REVIEW')}
        >
          <div className="metric-label">Under Review</div>
          <div className="metric-value" style={{ color: '#0284c7' }}>{totalCount}</div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="rep-app-filter-bar">
        <div className="filter-group">
          <label>Department / Discipline</label>
          <select 
            value={department} 
            onChange={(e) => { setDepartment(e.target.value); setPageIndex(1); }}
            disabled={!isAdmin && isRadiologist}
          >
            {isAdmin && <option value="">All Disciplines (Lab & Imaging)</option>}
            {(!isRadiologist && !isMicrobiologist) && (
              <option value="Pathology">Pathology (All Laboratory Specialties)</option>
            )}
            {(!isRadiologist) && (
              <option value="Microbiology">Pathology: Microbiology Workbench</option>
            )}
            {(isAdmin || isRadiologist) && (
              <option value="Radiology">Radiology & Diagnostic Imaging</option>
            )}
          </select>
          {!isAdmin && (isRadiologist || isPathologist || isMicrobiologist) && (
            <span style={{ fontSize: '0.72rem', color: '#0369a1', marginTop: '2px', fontWeight: 600 }}>
              🔒 {isRadiologist ? 'Radiology Imaging' : isPathologist ? 'Pathology Laboratory' : 'Pathology: Microbiology'} Specialty
            </span>
          )}
        </div>

        <div className="filter-group">
          <label>Workflow Status</label>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPageIndex(1); }}>
            <option value="">All Statuses</option>
            <option value="PENDING">Pending Review</option>
            <option value="UNDER_REVIEW">Under Review / Results Entered</option>
            <option value="CRITICAL">Critical Results Only</option>
            <option value="HELD">On Hold</option>
            <option value="APPROVED">Approved & Released</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Priority</label>
          <select value={priority} onChange={(e) => { setPriority(e.target.value); setPageIndex(1); }}>
            <option value="">All Priorities</option>
            <option value="Routine">Routine</option>
            <option value="Urgent">Urgent</option>
            <option value="STAT">STAT / Emergency</option>
          </select>
        </div>

        <div className="filter-group" style={{ flex: 1, minWidth: '220px' }}>
          <label>Search Patient / Accession / UHID / Test</label>
          <input
            type="text"
            placeholder="Type name, lab no, barcode..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPageIndex(1); }}
          />
        </div>

        {(department || status || priority || isCriticalOnly || searchTerm) && (
          <button
            type="button"
            style={{
              marginTop: '16px',
              padding: '6px 12px',
              border: 'none',
              background: '#f1f5f9',
              color: '#64748b',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem'
            }}
            onClick={() => {
              setDepartment(defaultDept);
              setStatus('');
              setPriority('');
              setIsCriticalOnly(false);
              setSearchTerm('');
              setPageIndex(1);
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Main Worklist Table */}
      <div className="rep-app-table-wrapper">
        <table className="rep-app-table">
          <thead>
            <tr>
              <th>Lab Accession</th>
              <th>Patient Information</th>
              <th>Investigation Name</th>
              <th>Department</th>
              <th>Priority</th>
              <th>Critical</th>
              <th>Status</th>
              <th>Entered By (Technician)</th>
              <th>Reporting Doctor</th>
              <th>Order Date</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  Loading approval queue...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  No reports matching current filter criteria.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.testId} className={item.isCritical ? 'critical-row' : ''}>
                  <td style={{ fontFamily: 'monospace', fontWeight: '700' }}>
                    {item.ledgerTransactionNo}
                  </td>
                  <td>
                    <div style={{ fontWeight: '700', color: '#0f172a' }}>{item.patientName}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                      {item.patientId} | {item.ageGender}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '600' }}>{item.itemName}</div>
                    {item.barcodeNo && (
                      <div style={{ fontSize: '0.72rem', color: '#64748b', fontFamily: 'monospace' }}>
                        SIN: {item.barcodeNo}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: '0.75rem', color: '#334155', fontWeight: '600' }}>
                      {item.department}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      fontWeight: '700',
                      fontSize: '0.72rem',
                      color: item.priority === 'STAT' ? '#ef4444' : item.priority === 'Urgent' ? '#f59e0b' : '#64748b'
                    }}>
                      {item.priority}
                    </span>
                  </td>
                  <td>
                    {item.isCritical ? (
                      <span className="status-badge badge-critical">⚠️ CRITICAL</span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                  <td>
                    {item.approved === 1 ? (
                      <span className="status-badge badge-approved">✓ Released</span>
                    ) : item.isHold === 1 ? (
                      <span className="status-badge badge-held">⏸ Held</span>
                    ) : (
                      <span className="status-badge badge-under-review">{item.workflowStatus}</span>
                    )}
                  </td>
                  <td>
                    <span style={{ color: '#0369a1', fontSize: '0.78rem' }}>
                      {item.resultEnteredBy ? `${item.resultEnteredBy}` : '—'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontWeight: '600', fontSize: '0.78rem', color: '#0f172a' }}>
                      {item.reportingDoctorName || item.approvedBy || 'Unassigned'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    {new Date(item.orderDate).toLocaleDateString()}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        type="button"
                        className="btn-action-review"
                        onClick={() => setReviewTestId(item.testId)}
                      >
                        {item.approved === 1 ? 'View Report' : 'Review & Release'}
                      </button>

                      {item.isHold === 1 && (
                        <button
                          type="button"
                          className="btn-action-unhold"
                          onClick={() => handleUnhold(item.testId)}
                        >
                          Unhold
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ marginTop: '16px' }}>
        <CommonPagination
          currentPage={pageIndex}
          totalItems={totalCount}
          pageSize={pageSize}
          onPageChange={setPageIndex}
        />
      </div>

      {/* Review Modal Dialog */}
      {reviewTestId && (
        <ReportReviewModal
          testId={reviewTestId}
          onClose={() => setReviewTestId(null)}
          onActionSuccess={(msg) => {
            showToastMsg(msg);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default ReportApprovalQueuePage;
